/**
*
* DualInfiniteScroll
* Keeps track of page number and selects auto or manual scroll
*/

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button } from 'react-bootstrap';
import Spinner from 'components/Spinner/index.jsx';
import useSafeState from 'utils/useSafeState.js';
import cx from 'utils/cx.js';
import sleep from 'utils/sleep.js';
import useTabbing from './useTabbing.js';
import messages from './messages.js';
import useScrollPosition from './useScrollPosition.js';
import useSentryLoading from './useSentryLoading.js';

function DualInfiniteScroll(props) {
  const { showItem, items, as, separator, className, disableScroll, bottomState } = props;
  const { before, after, hasMore, loadMore, hasNew, loadNew, loading, updating } = items;
  const [ready, setReady] = useSafeState(false);
  const tabbing = useTabbing();
  const { restoreScrollPosition, handleRootScroll, rootRef, scrollRef } = useScrollPosition();
  const rootNode = rootRef.current;
  const baseOptions = { root: rootNode };
  const initialRestore = useRef(false);

  async function restoreHandler() {
    if (!rootNode) {
      return;
    }
    if (loading || updating) {
      setReady(false);
      return;
    }
    // Next cycle, when rendering done, restore scroll position
    await sleep();
    if (!disableScroll) {
      restoreScrollPosition();
    }
    setReady(true);
    if (initialRestore.current) {
      return;
    }
    // Next cycle, when restored, enable scroll handler
    await sleep();
    initialRestore.current = true;
  }

  function scrollHandler() {
    if (initialRestore.current) {
      handleRootScroll();
    }
  }

  // Restore scroll position and set style when new items are added etc.
  useEffect(() => {
    restoreHandler();
  }, [loading, updating, bottomState, rootNode]); // eslint-disable-line react-hooks/exhaustive-deps

  const moreSentryRef = useSentryLoading({
    ...baseOptions,
    rootMargin: '200px 0px 0px 0px',
    enabled: hasMore && ready && !loading,
    loadMore,
  });

  const newSentryRef = useSentryLoading({
    ...baseOptions,
    rootMargin: '0px 0px 200px 0px',
    enabled: hasNew && ready && !updating,
    loadMore: loadNew,
  });

  const renderById = (id) => showItem(items[id]);

  const Component = as;
  const hasAfter = after && after.length > 0;
  return (
    <div ref={rootRef} className={cx('no-print-max-height', className)} onScroll={scrollHandler}>
      <Component className="d-flex flex-column">
        {!loading && tabbing && hasMore && (
          <footer className="p-4">
            <Button onClick={loadMore}>
              <FormattedMessage {...messages.loadPrevious} />
            </Button>
          </footer>
        )}
        {loading && <Spinner />}
        {!tabbing && <div className="scroll-sentry" ref={moreSentryRef} />}
        {before.map(renderById)}
      </Component>
      {hasAfter && separator}
      <div ref={scrollRef} />
      {hasAfter && (
        <Component className="d-flex flex-column">
          {after.map(renderById)}
          {!tabbing && <div className="scroll-sentry" ref={newSentryRef} />}
          {!updating && tabbing && hasNew && (
            <footer className="p-4">
              <Button onClick={loadNew}>
                <FormattedMessage {...messages.loadMore} />
              </Button>
            </footer>
          )}
        </Component>
      )}
    </div>
  );
}

DualInfiniteScroll.propTypes = {
  separator: PropTypes.node,
  items: PropTypes.object,
  as: PropTypes.any,
  showItem: PropTypes.func,
  className: PropTypes.string,
  disableScroll: PropTypes.bool,
  bottomState: PropTypes.bool,
};

export default DualInfiniteScroll;
